import moment from 'moment'
import { useState } from 'react'
import { useIntl } from 'react-intl'

import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Caption from 'LuxKit/Typography/Caption'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { rem } from 'polished'
import styled from 'styled-components'
import { getDayColor } from 'tripPlanner/utils/tripItemPin'

export const DayBadge = styled(Caption)`
  width: ${rem(48)};
  height: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.S};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${rem(8)};
`

interface Props {
  startDate: moment.Moment
  endDate: moment.Moment
  selectedDate?: moment.Moment
}

function DatesModal({
  startDate,
  endDate,
  selectedDate: initialSelectedDate,
}: Props) {
  const modalElement = useModalElementContext<
    moment.Moment | null | undefined
  >()

  const days = endDate.diff(startDate, 'days') + 1
  const clonedStartDate = startDate.clone().startOf('day')

  const intl = useIntl()
  const [selection, setSelection] = useState<moment.Moment | null | undefined>(
    initialSelectedDate,
  )

  return (
    <ModalBase height="auto">
      <ModalHeader
        title={intl.formatMessage({
          id: '3d2IN0MbbAynjRdsbMFk',
          defaultMessage: 'Move to',
          description: 'Modal title for selecting which day to move a trip item to',
        })}
        onCloseButtonClick={() => modalElement.resolve(undefined)}
      />
      <ModalBody>
        <ModalContent>
          {initialSelectedDate && (
            <ClickableListItem
              onClick={() => setSelection(null)}
              selected={selection === null}
              startIcon={<SolidHeartIcon colour="favourite" />}
              endIcon={selection === null ? <LineCheckIcon /> : undefined}
              title={intl.formatMessage({
                id: 'OEd7w5fMheIBVxaF3AyR',
                defaultMessage: 'Move to Saved',
                description: 'Option to move a trip item back to saved items from itinerary',
              })}
            />
          )}
          {[...Array(days).keys()].map((i) => {
            const currentDate = clonedStartDate.clone().add(i, 'day')
            const isSelected =
              moment.isMoment(selection) && selection.isSame(currentDate, 'day')
            const pinColor = getDayColor(currentDate)

            return (
              <ClickableListItem
                key={currentDate.unix()}
                onClick={() => setSelection(currentDate)}
                selected={isSelected}
                startIcon={<DayBadge
                  variant="medium"
                  weight="bold"
                  colour="neutral-eight"
                  format="uppercase"
                  style={{ backgroundColor: pinColor }}
                >
                  {currentDate.format('ddd')}
                </DayBadge>}
                title={currentDate.format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}
                endIcon={isSelected ? <LineCheckIcon /> : undefined}
              />
            )
          })}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'PuHj0r8kwp_QvNyKkIZl',
            defaultMessage: 'Save',
            description: 'Button to save the selected itinerary date in the itinerary',
          }),
          onClick: () => {
            modalElement.resolve(selection)
          },
          disabled: selection === undefined,
        }}
      />
    </ModalBase>
  )
}

export default DatesModal
